import React, { useMemo, useState } from "react";
import { useCurrentUserAccountsDetailsQuery, useSyncAccountTransactionsMutation } from "../generated/graphql";
import { bankLinkedAccountOptions } from "../utils/bankLinkedAccounts";
import ErrorMessage from "./ErrorMessage";

const DEFAULT_LIMIT = 500;

/**
 * Carte Admin : synchronise un seul compte bancaire en allant chercher jusqu'à `limit`
 * transactions, pour rattraper l'historique d'un compte sans relancer la synchro de tous.
 */
const SyncAccountCard: React.FC = () => {
  const { data, loading: loadingAccounts } = useCurrentUserAccountsDetailsQuery();
  const [syncAccountTransactions, { loading, error }] = useSyncAccountTransactionsMutation();
  const [accountId, setAccountId] = useState("");
  const [limit, setLimit] = useState(String(DEFAULT_LIMIT));
  const [createdCount, setCreatedCount] = useState<number | null>(null);

  const options = useMemo(
    () => bankLinkedAccountOptions(data?.currentUser?.accounts ?? [], data?.groups ?? []),
    [data],
  );
  const parsedLimit = Number(limit);
  const canSubmit = !!accountId && Number.isInteger(parsedLimit) && parsedLimit > 0 && !loading;

  const handleSync = async () => {
    setCreatedCount(null);
    try {
      const response = await syncAccountTransactions({ variables: { accountId, limit: parsedLimit } });
      setCreatedCount(response.data?.syncAccountTransactions.length ?? 0);
    } catch (err) {
      // L'erreur GraphQL est affichée via la prop `error` du hook
      console.error("Erreur lors de la synchronisation du compte:", err);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
      <h2 className="text-xl font-semibold mb-4">Synchroniser un compte</h2>
      <p className="text-gray-600 mb-4">
        Récupère depuis la banque jusqu'au nombre de transactions indiqué pour un seul compte, pour rattraper son historique. Les transactions déjà importées sont ignorées.
      </p>

      {error && (error.message.includes("account not linked to BudgetInside") ? (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">Ce compte n'est lié à aucune banque pour votre utilisateur : rien à synchroniser.</p>
        </div>
      ) : (
        <ErrorMessage error={error} onRetry={handleSync} title="Erreur lors de la synchronisation du compte" className="mb-4" />
      ))}

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="sync-account" className="block text-sm font-medium text-gray-700 mb-1">Compte</label>
          <select
            id="sync-account"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            disabled={loadingAccounts}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          >
            <option value="">{loadingAccounts ? "Chargement..." : "Choisir un compte lié à une banque"}</option>
            {options.map((option) => (
              <option key={option.accountId} value={option.accountId}>{option.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="sync-limit" className="block text-sm font-medium text-gray-700 mb-1">Nombre de transactions à récupérer</label>
          <input
            id="sync-limit"
            type="number"
            min={1}
            step={1}
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
        </div>
      </div>

      <button
        onClick={handleSync}
        disabled={!canSubmit}
        className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
      >
        {loading ? "Synchronisation en cours..." : "Synchroniser ce compte"}
      </button>

      {createdCount !== null && (
        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-800 font-medium">
            {createdCount > 0 ? `${createdCount} transaction(s) créée(s)` : "Aucune nouvelle transaction"}
          </p>
        </div>
      )}
    </div>
  );
};

export default SyncAccountCard;
